import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { downloadFromServer } from "@/lib/downloadBlob";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, Trash2, Eye, Brain, Layers, Pencil, UserPlus, BookCopy, Upload, Download, FileArchive, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import ConfirmDeleteDialog from "@/components/admin/ConfirmDeleteDialog";

const QUESTION_TYPES = [
  "Pattern Series Completion",
  "3x3 Matrix Reasoning",
  "Figure Analogy",
  "Odd-One-Out",
  "Mirror and Rotation Reasoning",
  "Shape Counting and Rule Integration",
  "Advanced Mixed Abstract Reasoning",
];
const BLOOM_LEVELS = ["Understand", "Apply", "Analyze", "Evaluate", "Create/Infer Rule"];
const DIFFICULTIES = ["Easy", "Moderate", "Difficult"];

// Mirrors backend DEFAULT_INSTRUCTIONS — used as a placeholder/fallback in the
// admin UI so legacy items (with no stored `instruction`) still show what the
// student will see if the admin leaves the field blank.
const DEFAULT_INSTRUCTIONS = {
  "Pattern Series Completion": "Choose the figure that best completes the visual series.",
  "3x3 Matrix Reasoning": "Study the 3x3 matrix and choose the figure that completes the missing cell.",
  "Figure Analogy": "Apply the same visual transformation: Figure A is to Figure B as Figure C is to which option?",
  "Odd-One-Out": "Choose the option that does NOT belong with the other figures.",
  "Mirror and Rotation Reasoning": "Choose the option that shows the target figure after the required transformation.",
  "Shape Counting and Rule Integration": "Use the counting and visual-rule pattern to choose the missing figure.",
  "Advanced Mixed Abstract Reasoning": "Identify the missing figure by integrating the row, column, position, shading, and rotation rules.",
};
const defaultInstructionFor = (qt) =>
  DEFAULT_INSTRUCTIONS[qt] || "Choose the option that correctly completes the figure.";

export default function AbstractExamRepositoryPage() {
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ question_type: "all", bloom_level: "all", difficulty: "all" });
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 25;
  const [view, setView] = useState(null);
  const [edit, setEdit] = useState(null);     // currently editing item
  const [editBusy, setEditBusy] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  // Assign dialog state — supports both course and student targets
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignTarget, setAssignTarget] = useState("course");  // "course" | "student"
  const [courses, setCourses] = useState([]);
  const [students, setStudents] = useState([]);
  const [assignCourse, setAssignCourse] = useState("");
  const [assignStudent, setAssignStudent] = useState("");
  const [assignCount, setAssignCount] = useState(100);
  const [assignMonths, setAssignMonths] = useState(0);
  const [assignBusy, setAssignBusy] = useState(false);
  const [assignments, setAssignments] = useState([]);
  // Import dialog state
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importReplace, setImportReplace] = useState(false);
  const [importBusy, setImportBusy] = useState(false);
  const [importResult, setImportResult] = useState(null);
  // Admin-configurable default per-session count for self-service enrollments
  const [defaultCount, setDefaultCount] = useState(60);
  const [defaultCountDraft, setDefaultCountDraft] = useState(60);
  const [savingDefaultCount, setSavingDefaultCount] = useState(false);

  const loadDefaultCount = () =>
    api.get("/admin/abstract-exam/default-count")
      .then((r) => { setDefaultCount(r.data.default_session_count); setDefaultCountDraft(r.data.default_session_count); })
      .catch(() => {});
  const saveDefaultCount = async () => {
    const n = parseInt(defaultCountDraft, 10);
    if (!Number.isFinite(n) || n < 1 || n > 1000) {
      toast.error("Must be a whole number between 1 and 1000");
      return;
    }
    setSavingDefaultCount(true);
    try {
      const r = await api.put("/admin/abstract-exam/default-count", { default_session_count: n });
      setDefaultCount(r.data.default_session_count);
      setDefaultCountDraft(r.data.default_session_count);
      toast.success("Default session count saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingDefaultCount(false);
    }
  };

  const loadStats = () => api.get("/admin/abstract-exam/stats").then((r) => setStats(r.data));
  const loadAssignments = () => api.get("/admin/abstract-exam/assignments").then((r) => setAssignments(r.data));
  const loadCourses = () => api.get("/courses").then((r) => setCourses(r.data));
  const loadStudents = () => api.get("/users/students").then((r) => setStudents(r.data));

  const loadRows = () => {
    setLoading(true);
    const params = { skip: page * PAGE_SIZE, limit: PAGE_SIZE };
    if (filters.question_type !== "all") params.question_type = filters.question_type;
    if (filters.bloom_level !== "all") params.bloom_level = filters.bloom_level;
    if (filters.difficulty !== "all") params.difficulty = filters.difficulty;
    api.get("/admin/abstract-exam/items", { params })
      .then((r) => { setRows(r.data.items); setTotal(r.data.total); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadStats(); loadAssignments(); loadCourses(); loadStudents(); loadDefaultCount(); }, []);
  useEffect(() => { loadRows(); /* eslint-disable-next-line */ }, [filters, page]);

  const openItem = async (row) => {
    const r = await api.get(`/admin/abstract-exam/items/${row.id}`);
    setView(r.data);
  };
  const openEdit = async (row) => {
    const r = await api.get(`/admin/abstract-exam/items/${row.id}`);
    setEdit(r.data);
  };
  const saveEdit = async () => {
    if (!edit) return;
    setEditBusy(true);
    try {
      // image_base64 is INTENTIONALLY omitted — the backend keeps the
      // existing image whenever this field is absent. Sending the huge
      // base64 string back on every edit would waste 13–20 KB per save.
      await api.put(`/admin/abstract-exam/items/${edit.id}`, {
        correct_answer: edit.correct_answer,
        rationale: edit.rationale || "",
        question_type: edit.question_type,
        bloom_level: edit.bloom_level,
        difficulty: edit.difficulty,
        instruction: edit.instruction || "",
        item_number: edit.item_number,
        status: edit.status || "active",
      });
      toast.success(`Item #${edit.item_number} saved`);
      setEdit(null);
      loadStats(); loadRows();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setEditBusy(false); }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    try {
      await api.delete(`/admin/abstract-exam/items/${deleting.id}`);
      toast.success("Item deleted");
      setDeleting(null);
      loadStats(); loadRows();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    } finally { setDeleteBusy(false); }
  };

  const submitAssign = async () => {
    if (assignTarget === "course" && !assignCourse) { toast.error("Pick a course"); return; }
    if (assignTarget === "student" && !assignStudent) { toast.error("Pick a student"); return; }
    setAssignBusy(true);
    try {
      if (assignTarget === "course") {
        await api.post("/admin/abstract-exam/assign-course", { course_id: assignCourse, count: Number(assignCount), subscription_months: Number(assignMonths) || 0 });
        toast.success(
          `Assigned ${assignCount} items to course. ${Number(assignMonths) > 0 ? `Access valid for ${Number(assignMonths)} month(s).` : "Lifetime access."} Students enrolled in this course now have access to the Abstract Exam module.`
        );
      } else {
        await api.post("/admin/abstract-exam/assign-student", { student_id: assignStudent, count: Number(assignCount), subscription_months: Number(assignMonths) || 0 });
        toast.success(
          `Assigned ${assignCount} items to student.${Number(assignMonths) > 0 ? ` Access valid for ${Number(assignMonths)} month(s).` : " Lifetime access."}`
        );
      }
      setAssignOpen(false);
      loadAssignments();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to assign");
    } finally { setAssignBusy(false); }
  };

  const removeAssignment = async (a) => {
    const who = a.scope === "course" ? `course "${a.course_name}"` : `student "${a.student_name}"`;
    if (!confirm(`Remove assignment of ${a.count} items from ${who}?`)) return;
    try {
      await api.delete(`/admin/abstract-exam/assignments/${a.id}`);
      toast.success("Assignment removed");
      loadAssignments();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const downloadTemplate = () => {
    downloadFromServer("/admin/abstract-exam/import-template", "abstract_exam_import_template.zip");
  };

  const submitImport = async () => {
    if (!importFile) { toast.error("Pick a .zip file first"); return; }
    setImportBusy(true); setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const r = await api.post(
        `/admin/abstract-exam/import-zip?replace_existing=${importReplace ? "true" : "false"}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 },
      );
      setImportResult(r.data);
      toast.success(`Imported ${r.data.inserted} item(s)`);
      // Refresh lists
      loadStats(); loadRows();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setImportBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="abstract-exam-repo-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-violet-600 dark:text-violet-400">Repository</div>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1 dark:text-slate-50">Abstract Exam Bank</h1>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">Manage the central pool of abstract-reasoning items. Assign any subset to a course or to a specific student.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            size="lg"
            variant="outline"
            onClick={() => { setImportFile(null); setImportResult(null); setImportReplace(false); setImportOpen(true); }}
            data-testid="open-import-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            Import
          </Button>
          <Button
            size="lg"
            className="bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white"
            onClick={() => {
              setAssignCount(Math.min(100, stats?.total || 100));
              setAssignCourse(""); setAssignStudent(""); setAssignTarget("course");
              setAssignOpen(true);
            }}
            data-testid="open-assign-btn"
          >
            <Layers className="w-4 h-4 mr-2" />
            Assign Abstract Exam
          </Button>
        </div>
      </div>

      {/* Default per-session count — used when a student self-enrolls via the
          "Abstract Exam" course and their request gets approved. Existing
          assignments are NOT retroactively changed. */}
      <Card className="p-4 border-slate-200 bg-gradient-to-br from-white to-violet-50/40" data-testid="ax-default-count-card">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-800">Default items per session</div>
            <div className="text-xs text-slate-500 mt-0.5">
              Applied when a student is auto-enrolled via the &quot;Abstract Exam&quot; course approval flow.
              Existing assignments are not affected — you can still change any individual one via <span className="font-semibold">Assign</span>.
              Currently: <span className="font-semibold text-slate-700">{defaultCount}</span> items.
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Input
              type="number"
              min={1}
              max={1000}
              value={defaultCountDraft}
              onChange={(e) => setDefaultCountDraft(e.target.value)}
              className="w-24"
              data-testid="ax-default-count-input"
            />
            <Button
              size="sm"
              onClick={saveDefaultCount}
              disabled={savingDefaultCount || parseInt(defaultCountDraft, 10) === defaultCount}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="ax-default-count-save"
            >
              {savingDefaultCount ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
            </Button>
          </div>
        </div>
      </Card>

      {/* Stats cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card className="p-4 border-slate-200">
            <div className="text-xs uppercase tracking-wider text-slate-500">Total Items</div>
            <div className="font-display text-3xl font-extrabold text-indigo-700 dark:text-indigo-300 mt-1">{stats.total}</div>
          </Card>
          <Card className="p-4 border-slate-200">
            <div className="text-xs uppercase tracking-wider text-slate-500">Question Types</div>
            <div className="font-display text-3xl font-extrabold text-violet-700 dark:text-violet-300 mt-1">{Object.keys(stats.by_type).length}</div>
          </Card>
          <Card className="p-4 border-slate-200">
            <div className="text-xs uppercase tracking-wider text-slate-500">Bloom Levels</div>
            <div className="font-display text-3xl font-extrabold text-emerald-700 dark:text-emerald-300 mt-1">{Object.keys(stats.by_bloom).length}</div>
          </Card>
          <Card className="p-4 border-slate-200">
            <div className="text-xs uppercase tracking-wider text-slate-500">Active Assignments</div>
            <div className="font-display text-3xl font-extrabold text-rose-700 dark:text-rose-300 mt-1">{assignments.length}</div>
          </Card>
        </div>
      )}

      {/* Active assignments table */}
      {assignments.length > 0 && (
        <Card className="p-5 border-slate-200">
          <h3 className="font-display font-bold mb-3 dark:text-slate-50">Active Assignments</h3>
          <div className="divide-y dark:divide-slate-700">
            {assignments.map((a) => (
              <div key={a.id} className="py-3 flex items-center justify-between gap-3" data-testid={`assignment-${a.id}`}>
                <div className="min-w-0">
                  <div className="font-semibold text-sm dark:text-slate-100">
                    {a.scope === "course" ? `Course: ${a.course_name}` : `Student: ${a.student_name}`}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">{a.count} items per session · {a.updated_at?.slice(0, 10)}</div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => removeAssignment(a)} className="text-rose-600">Remove</Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-end">
        <FilterSelect label="Question Type" value={filters.question_type} onChange={(v) => { setFilters({ ...filters, question_type: v }); setPage(0); }} options={QUESTION_TYPES} />
        <FilterSelect label="Bloom Level" value={filters.bloom_level} onChange={(v) => { setFilters({ ...filters, bloom_level: v }); setPage(0); }} options={BLOOM_LEVELS} />
        <FilterSelect label="Difficulty" value={filters.difficulty} onChange={(v) => { setFilters({ ...filters, difficulty: v }); setPage(0); }} options={DIFFICULTIES} />
        <div className="text-sm text-slate-500 ml-auto">Showing {rows.length} of {total}</div>
      </div>

      {/* List */}
      <Card className="border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-10 flex items-center justify-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-slate-500">No items match.</div>
        ) : (
          <div className="divide-y dark:divide-slate-700">
            {rows.map((row) => (
              <div key={row.id} className="px-5 py-3 flex items-center gap-4 hover:bg-slate-50 dark:hover:bg-slate-800/50" data-testid={`item-row-${row.id}`}>
                <div className="font-mono text-xs text-slate-400 w-12 shrink-0">#{row.item_number}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">{row.question_type}</div>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <Badge variant="outline" className="text-xs">{row.bloom_level}</Badge>
                    <Badge variant="outline" className={`text-xs ${row.difficulty === "Difficult" ? "border-rose-300 text-rose-700" : row.difficulty === "Moderate" ? "border-amber-300 text-amber-700" : "border-emerald-300 text-emerald-700"}`}>{row.difficulty}</Badge>
                    <span className="text-xs text-slate-500">Answer: <span className="font-bold">{row.correct_answer}</span></span>
                  </div>
                </div>
                <Button variant="ghost" size="icon" onClick={() => openItem(row)} title="View image" data-testid={`view-item-${row.id}`}>
                  <Eye className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => openEdit(row)} title="Edit item" data-testid={`edit-item-${row.id}`}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => setDeleting(row)} className="text-rose-600" title="Delete" data-testid={`delete-item-${row.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
        {total > PAGE_SIZE && (
          <div className="px-5 py-3 border-t flex items-center justify-between gap-3 dark:border-slate-700">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Previous</Button>
            <div className="text-sm text-slate-500">Page {page + 1} of {Math.ceil(total / PAGE_SIZE)}</div>
            <Button variant="outline" size="sm" disabled={(page + 1) * PAGE_SIZE >= total} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        )}
      </Card>

      {/* View dialog */}
      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-scroll abs-dialog-scroll" data-testid="item-view-dialog">
          <DialogHeader>
            <DialogTitle>Item #{view?.item_number} — {view?.question_type}</DialogTitle>
            <DialogDescription className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline">{view?.bloom_level}</Badge>
              <Badge variant="outline">{view?.difficulty}</Badge>
              <span>Correct answer: <span className="font-bold text-indigo-700">{view?.correct_answer}</span></span>
            </DialogDescription>
          </DialogHeader>
          {(view?.instruction || view?.question_type) && (
            <div className="p-3 rounded-md bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">Instruction shown to student</div>
              <p className="text-sm sm:text-base font-bold text-slate-900 dark:text-slate-50 leading-snug" data-testid="view-instruction">
                {view?.instruction || defaultInstructionFor(view?.question_type)}
              </p>
              {!view?.instruction && (
                <div className="mt-1 text-[11px] italic text-slate-500">No instruction stored — falling back to the default for "{view?.question_type}".</div>
              )}
            </div>
          )}
          {view?.image_base64 && (
            <div className="border rounded-lg overflow-hidden bg-white flex items-center justify-center">
              <img
                src={`data:image/png;base64,${view.image_base64}`}
                alt={`Item ${view.item_number}`}
                className="w-full h-auto max-h-[50vh] object-contain"
              />
            </div>
          )}
          {view?.rationale && (
            <div className="mt-2 p-3 rounded-md bg-indigo-50 dark:bg-indigo-950/30 text-sm text-slate-800 dark:text-slate-200">
              <div className="font-semibold mb-1 flex items-center gap-1.5"><Brain className="w-4 h-4 text-indigo-600" />Rationale</div>
              {view.rationale}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Assign dialog — target = course OR student */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent data-testid="assign-dialog">
          <DialogHeader>
            <DialogTitle>Assign Abstract Exam</DialogTitle>
            <DialogDescription>
              Grants access to the Abstract Exam module. Each session re-samples N items
              randomly from the repository, weighted by Bloom's taxonomy.
              <span className="block mt-1 font-semibold text-slate-700 dark:text-slate-300">No subject is created inside the course — Abstract Exam stays a standalone module.</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Target toggle */}
            <div className="grid grid-cols-2 gap-2 p-1 rounded-lg bg-slate-100 dark:bg-slate-800">
              <button
                type="button"
                className={`py-2 px-3 rounded-md text-sm font-bold flex items-center justify-center gap-2 transition-colors ${assignTarget === "course" ? "bg-white shadow-sm text-indigo-700 dark:bg-slate-900 dark:text-indigo-300" : "text-slate-500"}`}
                onClick={() => setAssignTarget("course")}
                data-testid="assign-target-course"
              >
                <BookCopy className="w-4 h-4" /> To Course
              </button>
              <button
                type="button"
                className={`py-2 px-3 rounded-md text-sm font-bold flex items-center justify-center gap-2 transition-colors ${assignTarget === "student" ? "bg-white shadow-sm text-indigo-700 dark:bg-slate-900 dark:text-indigo-300" : "text-slate-500"}`}
                onClick={() => setAssignTarget("student")}
                data-testid="assign-target-student"
              >
                <UserPlus className="w-4 h-4" /> To Student
              </button>
            </div>

            {assignTarget === "course" ? (
              <div>
                <label className="text-sm font-semibold">Course</label>
                <Select value={assignCourse} onValueChange={setAssignCourse}>
                  <SelectTrigger className="mt-1" data-testid="assign-course-select"><SelectValue placeholder="Choose a course" /></SelectTrigger>
                  <SelectContent>
                    {courses.map((c) => (<SelectItem key={c.id} value={c.id}>{c.course_name}</SelectItem>))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">All students enrolled in this course will get access.</p>
              </div>
            ) : (
              <div>
                <label className="text-sm font-semibold">Student</label>
                <Select value={assignStudent} onValueChange={setAssignStudent}>
                  <SelectTrigger className="mt-1" data-testid="assign-student-select"><SelectValue placeholder="Choose a student" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {students.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {(s.full_name || s.email)}{s.email ? ` · ${s.email}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">Per-student assignments override course-level assignments for that student.</p>
              </div>
            )}

            <div>
              <label className="text-sm font-semibold">Items per session</label>
              <Input type="number" min={1} max={stats?.total || 200} value={assignCount} onChange={(e) => setAssignCount(e.target.value)} className="mt-1" data-testid="assign-count-input" />
              <p className="text-xs text-slate-500 mt-1">Out of {stats?.total || 0} in the repository. The pool is NOT fixed — every session is a fresh random draw.</p>
            </div>

            <div>
              <label className="text-sm font-semibold">Access duration (months)</label>
              <Input
                type="number"
                min={0}
                max={60}
                value={assignMonths}
                onChange={(e) => setAssignMonths(Math.max(0, Math.min(60, parseInt(e.target.value || "0", 10))))}
                className="mt-1"
                data-testid="assign-months-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                <span className="font-semibold">0 = lifetime</span> (no expiry — default). Otherwise the student loses access N&nbsp;months from now and gets a 3-day warning notification + email.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignOpen(false)} disabled={assignBusy}>Cancel</Button>
            <Button onClick={submitAssign} disabled={assignBusy} data-testid="assign-submit-btn">
              {assignBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog — preserves the image, lets admin tweak metadata + answer */}
      <Dialog open={!!edit} onOpenChange={(o) => !o && !editBusy && setEdit(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-scroll abs-dialog-scroll" data-testid="item-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit Item #{edit?.item_number}</DialogTitle>
            <DialogDescription>The image is preserved. Update the metadata, answer, or rationale below.</DialogDescription>
          </DialogHeader>
          {edit?.image_base64 && (
            <div className="border rounded-md overflow-hidden bg-white flex items-center justify-center">
              <img
                src={`data:image/png;base64,${edit.image_base64}`}
                alt=""
                className="w-full h-auto max-h-[36vh] object-contain"
              />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Question Type</label>
              <Select value={edit?.question_type || ""} onValueChange={(v) => setEdit({ ...edit, question_type: v })}>
                <SelectTrigger className="mt-1" data-testid="edit-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {QUESTION_TYPES.map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Correct Answer</label>
              <Select value={edit?.correct_answer || ""} onValueChange={(v) => setEdit({ ...edit, correct_answer: v })}>
                <SelectTrigger className="mt-1" data-testid="edit-answer"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["A", "B", "C", "D"].map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Bloom Level</label>
              <Select value={edit?.bloom_level || ""} onValueChange={(v) => setEdit({ ...edit, bloom_level: v })}>
                <SelectTrigger className="mt-1" data-testid="edit-bloom"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {BLOOM_LEVELS.map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Difficulty</label>
              <Select value={edit?.difficulty || ""} onValueChange={(v) => setEdit({ ...edit, difficulty: v })}>
                <SelectTrigger className="mt-1" data-testid="edit-difficulty"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DIFFICULTIES.map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Instruction (shown above the image to the student)</label>
              <textarea
                value={edit?.instruction || ""}
                onChange={(e) => setEdit({ ...edit, instruction: e.target.value })}
                rows={2}
                placeholder={defaultInstructionFor(edit?.question_type)}
                className="mt-1 w-full rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
                data-testid="edit-instruction"
              />
              <div className="text-[11px] text-slate-500 mt-1">Leave blank to use the default for the selected question type.</div>
            </div>
            <div className="col-span-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Rationale</label>
              <textarea
                value={edit?.rationale || ""}
                onChange={(e) => setEdit({ ...edit, rationale: e.target.value })}
                rows={3}
                className="mt-1 w-full rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
                data-testid="edit-rationale"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)} disabled={editBusy}>Cancel</Button>
            <Button onClick={saveEdit} disabled={editBusy} data-testid="edit-save-btn">
              {editBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={!!deleting}
        onCancel={() => { if (!deleteBusy) setDeleting(null); }}
        onConfirm={confirmDelete}
        title="Delete this item?"
        itemLabel={`Item #${deleting?.item_number} (${deleting?.question_type})`}
        description="The item is removed permanently from the repository. Existing assignments are unaffected — their pool simply shrinks by one."
        busy={deleteBusy}
        confirmLabel="Delete item"
      />

      {/* Import dialog */}
      <Dialog open={importOpen} onOpenChange={(o) => !o && !importBusy && setImportOpen(o)}>
        <DialogContent className="max-w-xl" data-testid="import-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Upload className="w-5 h-5 text-indigo-600" />Import Abstract Exam Items</DialogTitle>
            <DialogDescription>
              Upload a <span className="font-semibold">.zip</span> file containing an <code>answers.csv</code>
              manifest and an <code>images/</code> folder. Need a starter? Download the template below.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <button
              type="button"
              onClick={downloadTemplate}
              className="w-full flex items-center gap-3 p-3 rounded-lg border border-dashed border-indigo-300 dark:border-indigo-700 bg-indigo-50/50 dark:bg-indigo-950/30 hover:bg-indigo-50 dark:hover:bg-indigo-950/50 transition-colors text-left"
              data-testid="download-template-btn"
            >
              <Download className="w-5 h-5 text-indigo-600 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-slate-900 dark:text-slate-100">Download import template</div>
                <div className="text-xs text-slate-500 dark:text-slate-400">Sample answers.csv + images folder + README. Fill it in and re-upload.</div>
              </div>
            </button>

            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">ZIP file</span>
              <div className="mt-1 flex items-center gap-2 p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
                <FileArchive className="w-5 h-5 text-slate-400 shrink-0" />
                <input
                  type="file"
                  accept=".zip,application/zip,application/x-zip-compressed"
                  onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                  className="text-sm flex-1 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-indigo-50 file:text-indigo-700 file:font-semibold hover:file:bg-indigo-100 dark:file:bg-indigo-950 dark:file:text-indigo-300"
                  data-testid="import-file-input"
                />
              </div>
              {importFile && (
                <div className="mt-1 text-xs text-slate-500">{importFile.name} · {(importFile.size / 1024 / 1024).toFixed(2)} MB</div>
              )}
            </label>

            <label className="flex items-start gap-2 p-3 rounded-lg border border-amber-200 dark:border-amber-700 bg-amber-50/60 dark:bg-amber-950/30 cursor-pointer">
              <input
                type="checkbox"
                checked={importReplace}
                onChange={(e) => setImportReplace(e.target.checked)}
                className="mt-0.5 accent-amber-600"
                data-testid="import-replace-toggle"
              />
              <div className="text-xs">
                <div className="font-bold text-amber-800 dark:text-amber-200">Replace ALL existing items first</div>
                <div className="text-amber-700/80 dark:text-amber-300/80 mt-0.5">Wipes the current {stats?.total ?? "?"} items before importing the new ones. Use this only when fully reseeding the repository.</div>
              </div>
            </label>

            {importResult && (
              <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 bg-white dark:bg-slate-900 text-sm" data-testid="import-result">
                <div className="font-bold flex items-center gap-1.5 mb-1.5">
                  <AlertCircle className="w-4 h-4 text-indigo-600" /> Import result
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="rounded bg-emerald-50 dark:bg-emerald-950/40 p-2">
                    <div className="text-lg font-bold text-emerald-700 dark:text-emerald-300">{importResult.inserted}</div>
                    <div className="text-[10px] uppercase font-bold text-emerald-700/80 dark:text-emerald-300/80">Inserted</div>
                  </div>
                  <div className="rounded bg-rose-50 dark:bg-rose-950/40 p-2">
                    <div className="text-lg font-bold text-rose-700 dark:text-rose-300">{importResult.skipped?.length || 0}</div>
                    <div className="text-[10px] uppercase font-bold text-rose-700/80 dark:text-rose-300/80">Skipped</div>
                  </div>
                  <div className="rounded bg-amber-50 dark:bg-amber-950/40 p-2">
                    <div className="text-lg font-bold text-amber-700 dark:text-amber-300">{importResult.replaced_existing || 0}</div>
                    <div className="text-[10px] uppercase font-bold text-amber-700/80 dark:text-amber-300/80">Replaced</div>
                  </div>
                </div>
                {importResult.skipped?.length > 0 && (
                  <div className="mt-2 max-h-32 overflow-y-auto text-xs text-slate-600 dark:text-slate-300">
                    {importResult.skipped.map((s) => (
                      <div key={s.row} className="py-0.5">Row {s.row}: {s.reason}</div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)} disabled={importBusy}>Close</Button>
            <Button onClick={submitImport} disabled={importBusy || !importFile} data-testid="import-submit-btn">
              {importBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Upload & Import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="mt-1 w-56"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All</SelectItem>
          {options.map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}
        </SelectContent>
      </Select>
    </div>
  );
}
